import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import RequestWizard, { STEPS, getStepSummary } from './elements/requestWizard.jsx';
import { AreaPreview } from './elements/areaMap.jsx';

const SEND_LABELS = { free: 'ارسال رایگان به مشاوران داخل اپلیکیشن', paid: 'ارسال پیامکی (غیررایگان) به تمام مشاوران منطقه' };
import RequestProgress from './elements/requestProgress.jsx';
import './requestUser.scss';

// درخواست‌ها در دیتابیسِ سرور، جدا برای هر کاربر (/api/requests). درخواست‌هایی که قبلاً فقط روی همین گوشی
// ذخیره شده بودند، با اولین باز شدنِ صفحه به سرور منتقل می‌شوند (با همان شناسه — دوباره ساخته نمی‌شوند)
const OLD_KEY = 'kh-requests';

async function api(path, method = 'GET', body) {
  let resp;
  try {
    resp = await fetch(`/api/requests${path}`, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new Error('اتصال به سرور برقرار نشد؛ اینترنت را بررسی کنید.');
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.message || 'خطایی پیش آمد؛ دوباره امتحان کنید.');
  return data;
}

// شکلِ ذخیره روی سرور: data = { formData, notes, sendMethod, targetCount }
const fromServer = (it) => ({ id: it.id, ...(it.data || {}), createdAt: it.createdAt });

async function migrateOld() {
  let old = [];
  try {
    old = JSON.parse(localStorage.getItem(OLD_KEY) || '[]');
  } catch (e) {
    old = [];
  }
  if (!Array.isArray(old) || !old.length) return;
  for (const r of old) {
    const { id, createdAt, ...data } = r;
    // eslint-disable-next-line no-await-in-loop
    await api('', 'POST', { id: typeof id === 'string' ? id : undefined, data, createdAt: typeof createdAt === 'number' ? createdAt : undefined });
  }
  try {
    localStorage.removeItem(OLD_KEY);
  } catch (e) {
    /* */
  }
}

function RequestUserPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  // درخواست‌هایی که همین الان ثبت شدند: فقط برای همین‌ها نوارِ «در حالِ ارسال» یک بار پر می‌شود
  const [fresh, setFresh] = useState(() => new Set());
  // حذف: اول تأییدِ کوتاه روی خودِ کارت؛ بعد از کاربر پنهان می‌شود. روی سرور فقط «حذف‌شده» علامت می‌خورد
  // (deleted_at) و اصلِ درخواست در دیتابیس می‌ماند
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  // جزئیاتِ کامل هر درخواست (با زدنِ فلشِ کارت باز و بسته می‌شود)
  const [openId, setOpenId] = useState(null);
  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api(`/${encodeURIComponent(id)}`, 'DELETE');
      setRequests((prev) => prev.filter((r) => r.id !== id));
      setError('');
    } catch (e) {
      setError(`درخواست حذف نشد — ${e.message}`);
    } finally {
      setDeleting(null);
      setConfirmId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      await migrateOld().catch(() => {});
      const r = await api('');
      setRequests((r.items || []).map(fromServer));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleCreate = async ({ form, notes, sendMethod, targetCount }) => {
    try {
      const r = await api('', 'POST', { data: { formData: form, notes, sendMethod, targetCount } });
      setRequests((prev) => [fromServer(r.item), ...prev]);
      setFresh((prev) => new Set(prev).add(r.item.id));
      setIsFormOpen(false);
      setError('');
    } catch (e) {
      setIsFormOpen(false);
      setError(`درخواست ثبت نشد — ${e.message}`);
    }
  };

  return (
    <div className="request-user-page">
      <button
        type="button"
        className="request-user-page__new-btn"
        onClick={() => setIsFormOpen(true)}
      >
        <Plus size={18} strokeWidth={2.2} />
        <span>ثبت درخواست جدید</span>
      </button>

      <section className="request-user-page__list">
        {requests.map((req) => {
          const formData = req.formData || {};
          const dealLabel = formData.dealType === 'sale' ? 'خرید' : 'اجاره';

          return (
            <article key={req.id} className="request-user-page__card">
              <div className="request-user-page__info">
                <button
                  type="button"
                  className="request-user-page__delete"
                  onClick={() => setConfirmId(confirmId === req.id ? null : req.id)}
                  aria-label="حذفِ درخواست"
                >
                  <Trash2 size={17} strokeWidth={1.9} />
                </button>
                <h3>
                  {dealLabel} {formData.propertyType === 'apartment' && 'آپارتمان'}
                  {formData.propertyType === 'villa' && 'ویلایی'}
                  {formData.propertyType === 'land' && 'زمین'}
                  {formData.propertyType === 'office' && 'ملک اداری'}
                  {formData.propertyType === 'shop' && 'مغازه'}
                </h3>

                {formData.dealType === 'sale' && (formData.priceFrom || formData.priceTo) && (
                  <p className="request-user-page__meta">
                    قیمت: {formData.priceFrom || '۰'} تا {formData.priceTo || '...'} تومان
                  </p>
                )}
                {formData.dealType === 'rent' && (formData.rentFrom || formData.rentTo) && (
                  <p className="request-user-page__meta">
                    اجاره: {formData.rentFrom || '۰'} تا {formData.rentTo || '...'} تومان
                  </p>
                )}
                {(formData.areaFrom || formData.areaTo) && (
                  <p className="request-user-page__meta">
                    متراژ: {formData.areaFrom || '۰'} تا {formData.areaTo || '...'} متر
                  </p>
                )}
                <button
                  type="button"
                  className="request-user-page__toggle"
                  onClick={() => setOpenId(openId === req.id ? null : req.id)}
                  aria-expanded={openId === req.id}
                  aria-label={openId === req.id ? 'بستنِ جزئیات' : 'نمایشِ جزئیات'}
                >
                  {openId === req.id ? <ChevronUp size={18} strokeWidth={1.9} /> : <ChevronDown size={18} strokeWidth={1.9} />}
                </button>
              </div>

              {openId === req.id && (
                <div className="request-user-page__details">
                  {STEPS.filter((s) => s.id !== 'location').map((s) => {
                    const summary = getStepSummary(s.id, formData);
                    const note = req.notes?.[s.id];
                    if (summary === 'انتخاب نشده' && !note) return null;
                    return (
                      <div key={s.id} className="request-user-page__detail">
                        <span className="request-user-page__detail-title">{s.title}</span>
                        <p>{summary}</p>
                        {note && <p className="request-user-page__detail-note">«{note}»</p>}
                      </div>
                    );
                  })}
                  {formData.location && (
                    <div className="request-user-page__detail">
                      <span className="request-user-page__detail-title">محدوده جغرافیایی</span>
                      <AreaPreview geojson={formData.location} />
                    </div>
                  )}
                  <div className="request-user-page__detail">
                    <span className="request-user-page__detail-title">نحوه ارسال</span>
                    <p>{SEND_LABELS[req.sendMethod] || SEND_LABELS.free}</p>
                  </div>
                </div>
              )}

              {confirmId === req.id && (
                <div className="request-user-page__confirm">
                  <span>این درخواست حذف شود؟</span>
                  <button type="button" className="is-danger" disabled={deleting === req.id} onClick={() => handleDelete(req.id)}>
                    {deleting === req.id ? 'در حالِ حذف…' : 'حذف'}
                  </button>
                  <button type="button" disabled={deleting === req.id} onClick={() => setConfirmId(null)}>
                    انصراف
                  </button>
                </div>
              )}

              <RequestProgress sendMethod={req.sendMethod || 'free'} targetCount={Number(req.targetCount) || 0} instant={!fresh.has(req.id)} />
            </article>
          );
        })}

        {loading && requests.length === 0 && <p className="request-user-page__empty">در حالِ بارگذاری…</p>}
        {error && (
          <p className="request-user-page__empty">
            {error}{' '}
            <button type="button" onClick={load}>
              تلاشِ دوباره
            </button>
          </p>
        )}
        {!loading && !error && requests.length === 0 && (
          <p className="request-user-page__empty">هنوز درخواستی ثبت نکرده‌اید</p>
        )}
      </section>

      {isFormOpen && (
        <RequestWizard
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}

export default RequestUserPage;